import { extensionStatus, type GraphqlErrorItem } from "$lib/api/errors";
import { GraphqlError } from "$lib/graphql/graphql-error";

type RecordLike = Record<string, unknown>;

export type MutationErrorPayload = {
  __typename: string;
  message: string;
  code?: string;
  details?: unknown;
};

export type MutationSuccessPayload = {
  __typename: string;
  message: string;
  [key: string]: unknown;
};

export function isMutationErrorPayload(value: unknown): value is MutationErrorPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as RecordLike;
  const typename = payload.__typename;
  if (typeof typename !== "string" || !typename.endsWith("Error")) return false;
  // Typename ending in Error is enough — some BE payloads omit `code`.
  if (typeof payload.message !== "string") return false;
  return true;
}

export function isMutationSuccessPayload(value: unknown): value is MutationSuccessPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as RecordLike;
  const typename = payload.__typename;
  if (typeof typename !== "string" || !typename.endsWith("Success")) return false;
  return typeof payload.message === "string";
}

function mutationErrorToGraphqlItem(payload: MutationErrorPayload): GraphqlErrorItem {
  const extensions: Record<string, unknown> = {
    code: typeof payload.code === "string" ? payload.code : "InternalError",
    details: payload.details ?? null,
  };
  const status = extensionStatus(extensions);
  if (status != null) extensions.status = status;
  return { message: payload.message, extensions };
}

/** Extract the Success member from a mutation payload union. */
export type MutationSuccessOf<T extends { __typename: string } = { __typename: string }> =
  Extract<T, { __typename: `${string}Success` }> extends never
    ? `${T["__typename"]}` extends `${string}Success`
      ? T
      : never
    : Extract<T, { __typename: `${string}Success` }>;

/** Throw ``GraphqlError`` for Error union members; otherwise return the Success payload. */
export function unwrapMutationPayload<T extends { __typename: string }>(
  payload: T | MutationErrorPayload | null | undefined,
): MutationSuccessOf<T> {
  if (payload == null) {
    throw new GraphqlError("Mutation returned no payload", []);
  }
  if (isMutationErrorPayload(payload)) {
    const item = mutationErrorToGraphqlItem(payload);
    throw new GraphqlError(payload.message, [item], extensionStatus(item.extensions) ?? undefined);
  }
  if (!isMutationSuccessPayload(payload)) {
    throw new GraphqlError("Mutation returned unexpected payload", []);
  }
  return payload as unknown as MutationSuccessOf<T>;
}

/** Deep-walk response data and throw on the first mutation Error payload. */
export function assertMutationPayloads(data: unknown, path = "data"): void {
  if (data == null || typeof data !== "object") return;

  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      assertMutationPayloads(data[i], `${path}[${i}]`);
    }
    return;
  }

  const record = data as RecordLike;
  if (isMutationErrorPayload(record)) {
    const item = mutationErrorToGraphqlItem(record);
    throw new GraphqlError(record.message, [item], extensionStatus(item.extensions) ?? undefined);
  }

  for (const [key, value] of Object.entries(record)) {
    assertMutationPayloads(value, `${path}.${key}`);
  }
}

/** Narrow a payload union to Success (typed; runtime errors throw via ``unwrapMutationPayload``). */
export function mutationSuccess<T extends { __typename: string }>(
  payload: T,
): MutationSuccessOf<T> {
  return unwrapMutationPayload(payload);
}
